import React from 'react';
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { GroupEnrollmentDetail } from '@models/grouping-agent';

const { Text } = Typography;

const CellBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 12, lineHeight: '18px', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{children}</div>
);

const formatAgeText = (r: GroupEnrollmentDetail, classStartDate?: string): string => {
  if (r.schoolYear?.trim()) return r.schoolYear.trim();
  const birthYear = r.birthYear?.trim();
  if (birthYear) {
    const y = Number.parseInt(birthYear, 10);
    if (!Number.isNaN(y) && classStartDate) {
      const classYear = new Date(classStartDate).getFullYear();
      if (!Number.isNaN(classYear)) return `${classYear - y + 1}세`;
    }
    return `${birthYear}년생`;
  }
  return '-';
};

export interface GroupEnrollmentMembersTableProps {
  members: GroupEnrollmentDetail[];
  onOpenSource?: (scheduleId: string) => void;
  loading?: boolean;
  classStartDate?: string;
}

export const buildGroupEnrollmentMemberColumns = (
  onOpenSource?: (scheduleId: string) => void,
  classStartDate?: string,
): ColumnsType<GroupEnrollmentDetail> => {
  const cols: ColumnsType<GroupEnrollmentDetail> = [
    {
      title: '참가자명',
      width: 120,
      fixed: 'left',
      render: (_, r) => {
        const name = r.participantName || r.agentDisplayName || r.scheduleName || '-';
        return (
          <Space direction="vertical" size={2} style={{ maxWidth: 110 }}>
            <Tooltip title={name}>
              <span style={{ fontWeight: 500 }}>{name}</span>
            </Tooltip>
            <Space size={2} wrap>
              {r.resolvedFromNotes && <Tag color="cyan">notes</Tag>}
              {r.namePending && <Tag color="orange">보충</Tag>}
            </Space>
          </Space>
        );
      },
    },
    {
      title: '나이',
      width: 100,
      render: (_, r) => (
        <Space size={4} wrap>
          <span>{formatAgeText(r, classStartDate)}</span>
          {r.birthYearResolvedFromNotes && <Tag color="cyan">notes</Tag>}
        </Space>
      ),
    },
    {
      title: '함께신청한 친구',
      width: 140,
      render: (_, r) => (
        <Space size={4} wrap style={{ maxWidth: 132 }}>
          <CellBlock>{r.friends || '-'}</CellBlock>
          {r.friendsResolvedFromNotes && <Tag color="cyan">notes</Tag>}
        </Space>
      ),
    },
    {
      title: '기타 입력',
      width: 80,
      render: (_, r) => (r.gender ? <CellBlock>{r.gender}</CellBlock> : <Text type="secondary">-</Text>),
    },
    {
      title: '구매자',
      width: 130,
      render: (_, r) => (
        <CellBlock>
          {[r.buyerName, r.buyerPhone].filter((v) => v != null && String(v).trim() !== '').join(' / ') || '-'}
        </CellBlock>
      ),
    },
    {
      title: '구매 정보',
      width: 160,
      render: (_, r) => (
        <CellBlock>
          {[
            r.sourceName || r.channel ? `플랫폼: ${r.sourceName || r.channel}` : undefined,
            r.optionName ? `옵션: ${r.optionName}` : undefined,
            r.orderUid ? `주문: ${r.orderUid}` : undefined,
          ]
            .filter((line): line is string => line != null)
            .join('\n') || '-'}
        </CellBlock>
      ),
    },
  ];

  if (onOpenSource) {
    cols.push({
      title: '',
      width: 72,
      fixed: 'right',
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => onOpenSource(r.scheduleId)}>
          원본
        </Button>
      ),
    });
  }

  return cols;
};

const GroupEnrollmentMembersTable: React.FC<GroupEnrollmentMembersTableProps> = ({
  members,
  onOpenSource,
  loading,
  classStartDate,
}) => (
  <Table
    size="small"
    rowKey="scheduleId"
    loading={loading}
    dataSource={members}
    columns={buildGroupEnrollmentMemberColumns(onOpenSource, classStartDate)}
    tableLayout="fixed"
    scroll={{ x: 720 }}
    pagination={members.length > 15 ? { pageSize: 15, hideOnSinglePage: true } : false}
  />
);

export default GroupEnrollmentMembersTable;
